import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, HandCoins, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, useApi, errMsg } from "@/pos/lib/api";
import { rm, dt, METHOD_LABEL } from "@/pos/lib/format";
import { Page, PageHeader, Empty } from "@/pos/components/Common";
import Field from "@/pos/components/Field";
import { CustomerDialog } from "@/pos/pages/Customers";

function EntryDialog({ kind, cid, balance, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("tunai");
  const isPay = kind === "bayaran";
  const save = async () => {
    try {
      await api.post(`/customers/${cid}/${isPay ? "payments" : "debts"}`, { amount: +amount, note, ...(isPay ? { method } : {}) });
      toast.success(isPay ? "Bayaran direkod" : "Hutang direkod");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="debt-entry-dialog">
        <DialogHeader><DialogTitle className="font-heading">{isPay ? "Rekod Bayaran Hutang" : "Rekod Hutang Baru"}</DialogTitle></DialogHeader>
        <Field label="Amaun (RM)"><Input type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 font-mono text-lg" data-testid="debt-amount-input" /></Field>
        {isPay && balance > 0 && <button onClick={() => setAmount(String(balance))} className="self-start rounded-full bg-slate-100 px-3 py-1 text-sm" data-testid="debt-pay-full-btn">Bayar penuh {rm(balance)}</button>}
        {isPay && (
          <div className="grid grid-cols-2 gap-2">
            {["tunai", "qr"].map((m) => (
              <button key={m} onClick={() => setMethod(m)} data-testid={`debt-method-${m}`}
                className={`rounded-lg border-2 py-2 text-sm font-semibold ${method === m ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200"}`}>{METHOD_LABEL[m]}</button>
            ))}
          </div>
        )}
        <Field label="Catatan"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={isPay ? "cth: Bayaran minggu 1" : "cth: Makanan majlis, bayar hujung bulan"} data-testid="debt-note-input" /></Field>
        <button onClick={save} disabled={!(+amount > 0)} className="btn-primary h-11 w-full disabled:opacity-40" data-testid="debt-save-btn">Simpan</button>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const { data: c, reload } = useApi(`/customers/${id}`);
  const [entry, setEntry] = useState(null);
  const [edit, setEdit] = useState(false);
  if (!c) return <Page><div className="text-slate-500">Memuatkan...</div></Page>;
  return (
    <Page>
      <Link to="/pos/pelanggan" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-customers"><ArrowLeft size={16} /> Semua pelanggan</Link>
      <PageHeader title={c.name} subtitle={c.phone || "Pelanggan"}>
        <button className="btn-outline" onClick={() => setEdit(true)} data-testid="edit-customer-btn"><Pencil size={16} /> Edit</button>
        <button className="btn-outline" onClick={() => setEntry("hutang")} data-testid="add-debt-btn"><Plus size={16} /> Rekod Hutang</button>
        <button className="btn-primary" onClick={() => setEntry("bayaran")} data-testid="debtors-record-payment-btn"><HandCoins size={16} /> Rekod Bayaran</button>
      </PageHeader>
      <div className="mb-6 flex items-center justify-between rounded-2xl hero-green p-6 text-white shadow-lg shadow-red-600/20">
        <div>
          <div className="text-xs uppercase tracking-wider text-red-50/90">Baki Hutang Semasa</div>
          <div className="num mt-1 text-4xl font-bold" data-testid="customer-balance">{rm(c.balance)}</div>
        </div>
        <HandCoins size={40} className="text-white/50" />
      </div>
      <h3 className="mb-3 font-heading text-lg font-bold">Rekod Hutang & Bayaran</h3>
      <div className="card-surface overflow-x-auto">
        <table className="data-table" data-testid="debt-ledger-table">
          <thead><tr><th>Tarikh</th><th>Jenis</th><th>Catatan</th><th>Oleh</th><th className="text-right">Amaun</th></tr></thead>
          <tbody>{c.entries.map((e) => (
            <tr key={e.id}>
              <td className="whitespace-nowrap text-slate-500">{dt(e.created_at)}</td>
              <td><span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${e.type === "hutang" ? "bg-amber-50 text-amber-700" : "bg-green-50 text-green-700"}`}>{e.type === "hutang" ? "Hutang" : `Bayaran · ${METHOD_LABEL[e.method] || ""}`}</span></td>
              <td>{e.note}</td>
              <td className="text-slate-500">{e.by}</td>
              <td className={`text-right font-mono font-bold ${e.type === "hutang" ? "text-amber-700" : "text-green-700"}`}>{e.type === "hutang" ? "+" : "−"}{rm(e.amount)}</td>
            </tr>
          ))}</tbody>
        </table>
        {c.entries.length === 0 && <Empty text="Tiada rekod hutang" />}
      </div>
      {c.sales.length > 0 && <>
        <h3 className="mb-3 mt-6 font-heading text-lg font-bold">Pembelian</h3>
        <div className="card-surface divide-y divide-slate-100">{c.sales.map((s) => (
          <button key={s.id} onClick={() => window.open(`/pos/cetak/invois/${s.id}`, "_blank")} className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50">
            <span className="font-mono text-sm font-semibold">{s.number} <span className="font-sans text-xs font-normal text-slate-500">{dt(s.created_at)}</span></span>
            <span className="font-mono font-bold">{rm(s.total)}</span>
          </button>
        ))}</div>
      </>}
      {entry && <EntryDialog kind={entry} cid={id} balance={c.balance} onClose={() => setEntry(null)} onSaved={() => { setEntry(null); reload(); }} />}
      <CustomerDialog open={edit} customer={c} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); reload(); }} />
    </Page>
  );
}
